import {
  ADD_CANDIDATE_TO_CAMPAIGN,
  GENERATE_BOOLEAN_STRINGS,
  GENERATE_MARKET_MAP,
  GENERATE_PERSONAS,
  GENERATE_ROLE_BLUEPRINT,
} from "@lib/graphql/mutations";
import { FIND_CANDIDATES_FOR_ROLE_QUERY } from "@lib/graphql/queries";
import { useChatStore } from "@lib/stores/chatStore";
import { CircleHelp, icons, Loader2, MessageSquare, UserPlus, Wand2, Zap } from "lucide-react";
import { useState } from "react";
import { useClient, useMutation } from "urql";

export interface StageAction {
  id: string;
  stage: number;
  actionType: string;
  label: string;
  icon: string | null;
  handlerType: "chat_prompt" | "app_operation" | "workflow";
  config: Record<string, unknown>;
  position: number;
  disabled: boolean;
  disabledReason: string | null;
}

interface StageActionBarProps {
  actions: StageAction[];
  campaignId: string;
  campaignTitle: string;
}

interface CandidateResult {
  id: string;
  fullName: string;
  currentRoleTitle: string | null;
  currentCompany: string | null;
  location: string | null;
}

interface OperationResults {
  actionId: string;
  candidates: CandidateResult[];
}

const OPERATION_REGISTRY: Record<
  string,
  {
    document: ReturnType<typeof import("urql")["gql"]>;
    buildVariables: (campaignId: string) => Record<string, unknown>;
  }
> = {
  find_candidates_for_role: {
    document: FIND_CANDIDATES_FOR_ROLE_QUERY,
    buildVariables: (campaignId) => ({ campaignId, limit: 20 }),
  },
};

const WORKFLOW_REGISTRY: Record<
  string,
  {
    document: ReturnType<typeof import("urql")["gql"]>;
    buildVariables: (campaignId: string) => Record<string, unknown>;
    resultKey: string;
  }
> = {
  generate_role_blueprint: {
    document: GENERATE_ROLE_BLUEPRINT,
    buildVariables: (campaignId) => ({ input: { campaignId } }),
    resultKey: "generateRoleBlueprint",
  },
  generate_personas: {
    document: GENERATE_PERSONAS,
    buildVariables: (campaignId) => ({ input: { campaignId } }),
    resultKey: "generatePersonas",
  },
  generate_market_map: {
    document: GENERATE_MARKET_MAP,
    buildVariables: (campaignId) => ({ input: { campaignId } }),
    resultKey: "generateMarketMap",
  },
  generate_boolean_strings: {
    document: GENERATE_BOOLEAN_STRINGS,
    buildVariables: (campaignId) => ({ input: { campaignId } }),
    resultKey: "generateBooleanStrings",
  },
};

function substituteTemplate(template: string, context: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (match, key) => context[key] ?? match);
}

function LucideIcon({ name, size = 16 }: { name: string | null; size?: number }) {
  if (!name) return <CircleHelp size={size} />;
  const IconComponent = icons[name as keyof typeof icons];
  if (!IconComponent) return <CircleHelp size={size} />;
  return <IconComponent size={size} />;
}

export function StageActionBar({ actions, campaignId, campaignTitle }: StageActionBarProps) {
  const client = useClient();
  const chatOpen = useChatStore((s) => s.open);
  const chatCreateThread = useChatStore((s) => s.createThread);
  const chatStartNew = useChatStore((s) => s.startNewChat);
  const [loadingActionId, setLoadingActionId] = useState<string | null>(null);
  const [operationResults, setOperationResults] = useState<OperationResults | null>(null);
  const [, addToCampaign] = useMutation(ADD_CANDIDATE_TO_CAMPAIGN);
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set());

  if (actions.length === 0) return null;

  const sorted = [...actions].sort((a, b) => a.position - b.position);

  const templateContext: Record<string, string> = {
    campaign_name: campaignTitle,
  };

  function handleChatPrompt(action: StageAction) {
    const template = action.config.prompt_template as string | undefined;
    if (!template) return;
    const prompt = substituteTemplate(template, templateContext);
    chatOpen();
    chatCreateThread(prompt);
  }

  async function handleWorkflow(action: StageAction) {
    const mutationName = action.config.mutation as string | undefined;
    if (!mutationName) return;

    const entry = WORKFLOW_REGISTRY[mutationName];
    if (!entry) {
      console.warn(`[StageActionBar] Unknown workflow mutation: ${mutationName}`);
      return;
    }

    setLoadingActionId(action.id);
    try {
      const result = await client
        .mutation(entry.document, entry.buildVariables(campaignId))
        .toPromise();
      const payload = (
        result.data as Record<string, { enqueued?: boolean; errors?: string[] }> | undefined
      )?.[entry.resultKey];
      if (result.error) {
        console.error("[StageActionBar] Workflow mutation error:", result.error);
        return;
      }
      if (payload?.errors && payload.errors.length > 0) {
        console.error("[StageActionBar] Workflow returned errors:", payload.errors);
        return;
      }
      if (action.config.follow_up === "open_chat") {
        chatStartNew();
        chatOpen();
      }
    } catch (e) {
      console.error("[StageActionBar] Workflow dispatch failed:", e);
    } finally {
      setLoadingActionId(null);
    }
  }

  async function handleAppOperation(action: StageAction) {
    const operationName = action.config.operation as string | undefined;
    if (!operationName) return;

    const entry = OPERATION_REGISTRY[operationName];
    if (!entry) {
      console.warn(`[StageActionBar] Unknown operation: ${operationName}`);
      return;
    }

    setLoadingActionId(action.id);
    try {
      const result = await client
        .query(entry.document, entry.buildVariables(campaignId))
        .toPromise();
      const data = result.data as Record<string, unknown> | undefined;
      if (data?.findCandidatesForRole) {
        setOperationResults({
          actionId: action.id,
          candidates: data.findCandidatesForRole as CandidateResult[],
        });
      }
    } catch (e) {
      console.error("[StageActionBar] Operation failed:", e);
    } finally {
      setLoadingActionId(null);
    }
  }

  function handleClick(action: StageAction) {
    if (action.handlerType === "chat_prompt") {
      handleChatPrompt(action);
    } else if (action.handlerType === "workflow") {
      handleWorkflow(action);
    } else {
      handleAppOperation(action);
    }
  }

  async function handleAddCandidate(candidateId: string) {
    await addToCampaign({
      input: { campaignId, candidateId, stage: "longlist", source: "ai_match" },
    });
    setAddedIds(new Set([...addedIds, candidateId]));
  }

  return (
    <div className="mt-3">
      <div className="flex items-center gap-2">
        {sorted.map((action) => {
          const isChatPrompt = action.handlerType === "chat_prompt";
          const isWorkflow = action.handlerType === "workflow";
          const isLoading = loadingActionId === action.id;
          const isDisabled = action.disabled;
          const tooltip =
            isDisabled && action.disabledReason ? action.disabledReason : action.label;

          return (
            <button
              key={action.id}
              type="button"
              title={tooltip}
              aria-disabled={isDisabled}
              disabled={isLoading || isDisabled}
              onClick={() => handleClick(action)}
              className={`flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${
                isChatPrompt
                  ? "border border-border bg-surface text-text-secondary hover:bg-card-hover"
                  : "bg-accent/10 text-accent hover:bg-accent/20 dark:bg-accent/20 dark:hover:bg-accent/30"
              } ${isLoading ? "cursor-wait opacity-60" : ""} ${
                isDisabled
                  ? "cursor-not-allowed opacity-50 hover:bg-transparent dark:hover:bg-transparent"
                  : ""
              }`}
            >
              {isLoading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <LucideIcon name={action.icon} size={14} />
              )}
              <span>{action.label}</span>
              {isChatPrompt ? (
                <MessageSquare size={10} className="ml-0.5 text-text-faint" />
              ) : isWorkflow ? (
                <Wand2 size={10} className="ml-0.5" />
              ) : (
                <Zap size={10} className="ml-0.5" />
              )}
            </button>
          );
        })}
      </div>

      {operationResults && operationResults.candidates.length > 0 && (
        <div className="mt-3 rounded-lg border border-border p-4">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="text-sm font-medium text-text-secondary">
              Role-Matched Candidates ({operationResults.candidates.length})
            </h4>
            <button
              type="button"
              onClick={() => setOperationResults(null)}
              className="text-xs text-text-faint hover:text-text-secondary"
            >
              Close
            </button>
          </div>
          <div className="space-y-1">
            {operationResults.candidates.map((c) => (
              <div
                key={c.id}
                className="flex items-center gap-3 rounded border border-border px-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <span className="text-sm font-medium text-text-primary">{c.fullName}</span>
                  {(c.currentRoleTitle || c.currentCompany) && (
                    <span className="ml-2 text-xs text-text-muted">
                      {[c.currentRoleTitle, c.currentCompany].filter(Boolean).join(" at ")}
                    </span>
                  )}
                  {c.location && <span className="ml-2 text-xs text-text-faint">{c.location}</span>}
                </div>
                {addedIds.has(c.id) ? (
                  <span className="text-xs text-green-600">Added</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleAddCandidate(c.id)}
                    className="flex items-center gap-1 rounded px-2 py-1 text-xs text-accent hover:bg-accent/5"
                  >
                    <UserPlus size={12} /> Add
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
